'use client';
import clsx from 'clsx';
import { Check, Edit, Plus, Trash } from 'lucide-react';
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';

import { Pet } from '@/db/queries/pets';
import { useLocalforage } from '@/hooks/use-localforage';
import { usePets } from '@/hooks/use-pets';
import { getSession } from '@/hooks/use-session/session-actions';
import { useSession } from '@/hooks/use-session/use-session';
import { getEmailError, normalizeEmail } from '@/lib/email-typo';
import { gtagEvent } from '@/utils/gtag';
import { structuredCloneWithFallback } from '@/utils/structured-clone-with-fallback';
import { wait } from '@/utils/wait';

import { useIsDevUser } from '../dev';
import { closeHighestDrawer } from '../drawer';
import { deletePet, getPets } from '../patient/patient-actions';
import { PetEditButton } from '../patient/pet-edit-button';
import { Button } from '../ui/button';
import { EmailTypoHint } from '../ui/email-typo-hint';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../ui/select';
import { useToast } from '../ui/use-toast';
import quizQuestions from './quiz.json';
import { forceAnonymousLogIn, saveQuiz, saveQuizLead } from './quiz-actions';
import { getQuiz } from './quiz-actions';
import { answerSchema, quizInitialState, QuizKey, QuizState } from './quiz-schema';
import {
  getDependentQuestion,
  getIsPhoneNumberValid,
  getIsQuestionInvalid,
  getStepQuestions,
  QuizQuestion,
  QuizStepIndex,
} from './quiz-steps';

export const LOCALFORAGE_KEY_QUIZ = 'quiz';

type QuizFormProps = {
  step: QuizStepIndex;
  scrollToKey?: string;
  setSubmitting: Dispatch<SetStateAction<boolean>>;
};

export function QuizForm({ step, scrollToKey, setSubmitting }: QuizFormProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const isDevUser = useIsDevUser();
  const session = useSession();
  const { toast } = useToast();
  const {
    values: [quiz],
    setItem,
    store,
  } = useLocalforage<[QuizState]>([LOCALFORAGE_KEY_QUIZ]);
  const localQuizState = quiz ?? quizInitialState;
  const localQuizStateRef = useRef(localQuizState);
  // Judged whenever the field is not being typed in, so a half-typed address is never scolded
  // but one restored from a previous visit explains itself straight away.
  const [isEmailFocused, setIsEmailFocused] = useState(false);
  const { pets, updatePets } = usePets();

  localQuizStateRef.current = localQuizState;

  /**
   * Fire and forget on blur once the address looks real, so someone who leaves after the email
   * question is still reachable. Never blocks the field, and a failure is only logged.
   */
  const onEmailBlur = useCallback(() => {
    setIsEmailFocused(false);

    const quizState = localQuizStateRef.current;
    const email = quizState.answers.find((a) => a.key === 'email')?.text;

    if (email && !getEmailError(email)) {
      saveQuizLead(quizState).catch((error) => console.error('Early email capture failed', error));
    }
  }, []);

  const setQuizItem = useCallback(
    async (questionText: string, answerText: string) => {
      try {
        const quizState = structuredCloneWithFallback<typeof localQuizState>(localQuizState);
        const flatQuizQuestions = quizQuestions.reduce<QuizQuestion[]>((acc, curr) => {
          const { dependentQuestions, ...currentQuestion } = curr;

          const answerText = quizState.answers.find((a) => a.question === curr.question)?.text;

          acc.push(currentQuestion);

          if (dependentQuestions) {
            const dependentQuestion = dependentQuestions.find((d) => d.condition === answerText);

            dependentQuestion && acc.push({ diagnosis: '', ...dependentQuestion });
          }

          return acc;
        }, []);
        const originalQuestion = flatQuizQuestions.find((q) => q.question === questionText);
        const hasAnswers = originalQuestion?.answers?.length;
        const answerIndex = hasAnswers ? (originalQuestion?.answers?.findIndex((a) => a === answerText) ?? -1) : -1;
        const points = hasAnswers && answerIndex !== -1 ? (originalQuestion?.points?.[answerIndex] ?? 0) : 0;
        const calculatedAnswer = answerSchema.parse({
          question: questionText,
          text: answerText,
          points,
          key: originalQuestion?.key,
        });
        const questionIndex = quizState.answers.findIndex((a) => a.question === questionText);

        // Reset city answer if state is changed
        if (calculatedAnswer.key === 'state') {
          const cityIndex = quizState.answers.findIndex((a) => a.key === 'city');

          if (cityIndex !== -1) {
            quizState.answers[cityIndex].text = '';
          }
        }

        if (questionIndex !== -1) {
          quizState.answers[questionIndex] = calculatedAnswer;
        } else {
          quizState.answers.push(calculatedAnswer);
        }

        const requiredCount = getRequiredCount(quizState);
        const requiredAnsweredCount = getRequiredAnsweredCount(quizState);
        const phoneQuestion = quizState.answers.find((a) => a.key === 'phone_number');
        const quizPoints = quizState.answers.reduce((acc, curr) => acc + curr.points, 0);
        const isValid =
          requiredAnsweredCount >= requiredCount && quizPoints > 0 && getIsPhoneNumberValid(phoneQuestion?.text);

        quizState.points = quizPoints;
        quizState.isValid = isValid;

        await setItem(LOCALFORAGE_KEY_QUIZ, quizState);
      } catch (error) {
        console.error(error);
      }
    },
    [localQuizState, setItem]
  );
  const onSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setSubmitting(true);

      // No session here means the anonymous one from opening the drawer never landed. Create it
      // rather than sending an anonymous visitor to an email sign-in page mid-questionnaire.
      if (!(await getSession())) await forceAnonymousLogIn();

      const quizState = structuredCloneWithFallback(localQuizState);

      if (quizState.isValid) {
        quizState.submitted = new Date().toISOString();

        // Awaited for the grace period inside gtagEvent: location.href below tears down any
        // beacon the GTM conversion tag has not managed to send yet.
        await gtagEvent('submit_quiz');

        await setItem(LOCALFORAGE_KEY_QUIZ, quizState);

        await saveQuiz(quizState);

        location.href = '/user/dashboard';
      } else {
        toast({
          title: 'Questionnaire validation failed',
          description: 'Some required questions are not answered or have invalid answers.',
        });
        console.error(`quizState is not valid: ${JSON.stringify(quizState)}`);
      }
      setSubmitting(false);
    },
    [localQuizState, setItem, setSubmitting, toast]
  );

  useEffect(() => {
    if (quiz && scrollToKey) {
      scrollToQuizKey(scrollToKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!quiz]);

  // Each step starts at its own top rather than wherever the previous step was scrolled to.
  useEffect(() => {
    rootRef.current?.closest('.content')?.scrollTo({ top: 0 });
  }, [step]);

  /**
   * Lives here rather than in PetsSection so hasPets stays in sync while step 1 is unmounted,
   * e.g. when the drawer reopens straight onto step 2 or 3.
   */
  useEffect(() => {
    if (store?.ready) {
      store.getItem<QuizState>(LOCALFORAGE_KEY_QUIZ).then((quiz) => {
        const quizOrDefault = { ...quizInitialState, ...quiz };
        const hasPets = !!pets.length;

        // usePets starts empty and fills in a round trip later. Writing unconditionally meant
        // every open wrote hasPets:false first, which could land on top of the server
        // rehydration below and throw away a restored questionnaire.
        if (quizOrDefault.hasPets === hasPets) return;

        setItem(LOCALFORAGE_KEY_QUIZ, { ...quizOrDefault, hasPets });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pets]);

  useEffect(() => {
    if (session?.user?.id && store)
      Promise.all([getQuiz(), store.getItem<QuizState>(LOCALFORAGE_KEY_QUIZ)]).then(async ([form, local]) => {
        /**
         * Adopt the server copy when nothing is in progress locally, or when it holds a newer
         * submission than this device has seen, e.g. one made from another device. A lead from
         * early email capture never wins over local answers given after that capture.
         */
        const isNewerSubmission = !!form?.submitted && (!local?.submitted || form.submitted > local.submitted);

        if (form?.answers && (!local?.answers?.length || isNewerSubmission)) {
          const pets = await getPets();
          const answers = form.answers as Record<QuizKey, { question: string; text: string }>;
          const quizState = { ...quizInitialState, answers: Object.values(answers), hasPets: !!pets.length };

          /**
           * This will leave the quiz in an invalid state until the TOS question gets reset 👇.
           */
          await setItem(LOCALFORAGE_KEY_QUIZ, quizState);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id, !!store]);

  useEffect(() => {
    const isUnvalidatedQuiz = localQuizState.points === 0 && localQuizState.answers.length > 0;

    if (isUnvalidatedQuiz) {
      /**
       * Need to run the setQuizItem function at least once to validate the quiz.
       * The TOS question is a reliable way to do that. It always has an answer.
       */
      const tosAnswer = localQuizState.answers.find((quiz) => quiz.key === 'terms_of_service');

      tosAnswer && setQuizItem(tosAnswer.question, tosAnswer.text);
    }
  }, [localQuizState, setQuizItem]);

  return (
    <div id='quiz-form' ref={rootRef}>
      {step === 0 && <PetsSection pets={pets} updatePets={updatePets} />}
      <form className='flex flex-col gap-8' onSubmit={onSubmit}>
        {getStepQuestions(step).map(
          ({ dependentQuestions, key, type, question, answers = [], hint, placeholder, required }) => {
            const existingAnswer = localQuizState.answers.find((a) => a.question === question);
            const isInvalid = getIsQuestionInvalid({ key, required }, existingAnswer);
            const emailError = key === 'email' ? getEmailError(existingAnswer?.text || '') : null;
            // A negative score is a stop, not a wrong answer. Without a reason on screen the
            // questionnaire just refuses to advance and never says why.
            const isBlockingAnswer = !!existingAnswer && existingAnswer.points < 0;
            const isSuicideQuestion = key === 'suicide';
            const isTosQuestion = key === 'terms_of_service';
            const isDev = window.location.href.includes('localhost') || isDevUser;
            const [dependentQuestion] =
              dependentQuestions?.filter(({ condition }) => existingAnswer?.text === condition) || [];

            if (isDev && key === 'state' && !answers.includes('Testizona')) {
              answers.unshift('Testizona');
            }
            return (
              <div
                className='relative'
                key={question}
                data-answer={existingAnswer?.text || 'no_answer'}
                data-blocking={isInvalid}
                data-key={key}
                data-question={question}
              >
                <div className='relative flex flex-col gap-3 z-10'>
                  <div className='flex flex-col'>
                    <div className='flex justify-between'>
                      <span className='flex-1'>{question}</span>
                      {required && (
                        <Check
                          className={clsx({
                            'text-slate-300': !existingAnswer,
                            'text-red-600': isInvalid && existingAnswer,
                            'text-green-600': !isInvalid,
                          })}
                        />
                      )}
                    </div>
                    {isSuicideQuestion && (
                      <span className='text-sm'>
                        Suicide hotline:{' '}
                        <a href='https://988lifeline.org/' style={{ textDecoration: 'underline' }}>
                          https://988lifeline.org/
                        </a>
                      </span>
                    )}
                    {isTosQuestion && (
                      <div className='flex flex-col gap-2 py-2'>
                        <span className='text-sm'>
                          <a href='/terms-and-conditions' style={{ textDecoration: 'underline' }} target='_blank'>
                            Review Terms of Service
                          </a>
                        </span>
                        <span className='text-sm'>
                          <a href='/privacy' style={{ textDecoration: 'underline' }} target='_blank'>
                            Review Privacy Policy
                          </a>
                        </span>
                      </div>
                    )}
                    <span className='text-sm'>{hint}</span>
                  </div>
                  {type === 'multiple-choice' && (
                    <div className={clsx('grid gap-2', getAnswerGridClassName(answers.length))}>
                      {answers.map((answer) => {
                        const isSuicideAnswer = isSuicideQuestion && answer === 'Yes';
                        const isSelected = existingAnswer?.text === answer;
                        const isOptional = !required;
                        const shouldDeselect = isOptional && isSelected;

                        return (
                          <Button
                            aria-pressed={isSelected}
                            className={ANSWER_BUTTON_CLASS_NAME}
                            key={question + answer}
                            onClick={(e) => {
                              e.preventDefault();

                              if (isSuicideAnswer) {
                                alert('Please call 988 for the suicide hotline.');
                              }

                              if (shouldDeselect) {
                                setQuizItem(question, '');
                              } else {
                                setQuizItem(question, answer);
                              }
                            }}
                            variant={isSelected ? 'default' : 'outline'}
                          >
                            {answer}
                          </Button>
                        );
                      })}
                    </div>
                  )}

                  {isBlockingAnswer && (
                    <p className='text-sm text-red-600'>{BLOCKING_ANSWER_MESSAGES[key] ?? BLOCKING_ANSWER_FALLBACK}</p>
                  )}

                  {type === 'select' && (
                    <div>
                      <Select value={existingAnswer?.text} onValueChange={(answer) => setQuizItem(question, answer)}>
                        <SelectTrigger className='h-11 text-base sm:text-sm'>
                          <SelectValue placeholder='State...' />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectLabel>State</SelectLabel>
                            {answers.map((answer) => (
                              <SelectItem key={answer} value={answer}>
                                {answer}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {type === 'email' && (
                    <div>
                      <p className='text-sm pb-2'>
                        We send your sign-in link to this address, so it needs to be one you can open.
                      </p>
                      <Input
                        value={existingAnswer?.text || ''}
                        name={question}
                        onChange={(e) => setQuizItem(question, normalizeEmail(e.target.value))}
                        autoComplete='email'
                        onBlur={onEmailBlur}
                        onFocus={() => setIsEmailFocused(true)}
                        className={INPUT_CLASS_NAME}
                        inputMode='email'
                        type='email'
                        placeholder={placeholder}
                      />

                      {!isEmailFocused && !!emailError && <p className='text-sm text-red-600 pt-1'>{emailError}</p>}

                      <EmailTypoHint
                        email={existingAnswer?.text || ''}
                        onAccept={(corrected) => setQuizItem(question, corrected)}
                      />
                    </div>
                  )}

                  {(type === 'text' || type === 'number') && (
                    <div>
                      <Input
                        value={existingAnswer?.text || ''}
                        name={question}
                        onChange={(e) => setQuizItem(question, e.target.value)}
                        autoComplete={AUTOCOMPLETE[key]}
                        className={INPUT_CLASS_NAME}
                        inputMode={key === 'phone_number' ? 'tel' : undefined}
                        type={key === 'phone_number' ? 'tel' : type}
                        placeholder={placeholder}
                      />
                    </div>
                  )}

                  {!!dependentQuestion && (
                    <DependentQuestion
                      key={dependentQuestion.question}
                      dependentQuestion={dependentQuestion}
                      existingAnswer={localQuizState.answers.find((a) => a.question === dependentQuestion.question)}
                      setQuizItem={setQuizItem}
                    />
                  )}
                </div>
              </div>
            );
          }
        )}
        <button className='hidden' id='quiz-submit' tabIndex={-1} type='submit' />
      </form>
    </div>
  );
}

type DependentQuestionProps = {
  dependentQuestion: {
    key: string;
    type: string;
    condition: string;
    required: boolean;
    question: string;
    answers: string[];
  };
  existingAnswer?: {
    text: string;
    points: number;
    key: string | null;
    question: string;
  };
  setQuizItem: any;
};

function DependentQuestion({ dependentQuestion, existingAnswer, setQuizItem }: DependentQuestionProps) {
  // Required once shown: see the note in getIsStepComplete.
  const isInvalid = getIsQuestionInvalid({ ...dependentQuestion, required: true }, existingAnswer);

  return (
    <div
      className='relative z-10 flex flex-col gap-3 pt-3'
      data-answer={existingAnswer?.text || 'no_answer'}
      data-blocking={isInvalid}
      data-key={dependentQuestion.key}
      data-question={dependentQuestion.question}
    >
      <div className='flex justify-between'>
        <span className='flex-1'>{dependentQuestion.question}</span>
        {
          <Check
            className={clsx({
              'text-slate-300': !existingAnswer,
              'text-red-600': isInvalid && existingAnswer,
              'text-green-600': !isInvalid,
            })}
          />
        }
      </div>

      <div className={clsx('grid gap-2', getAnswerGridClassName(dependentQuestion.answers.length))}>
        {dependentQuestion.answers.map((answer) => {
          const isSelected = existingAnswer?.text === answer;

          return (
            <Button
              aria-pressed={isSelected}
              className={ANSWER_BUTTON_CLASS_NAME}
              key={dependentQuestion.key + answer}
              onClick={(e) => {
                e.preventDefault();

                setQuizItem(dependentQuestion.question, answer);
              }}
              variant={isSelected ? 'default' : 'outline'}
            >
              {answer}
            </Button>
          );
        })}
      </div>
    </div>
  );
}

function getRequiredAnsweredCount(quizState: QuizState) {
  const requiredAnsweredTopLevel = quizQuestions.filter(
    (q) => q.required && quizState.answers.find((a) => a.key === q.key) && !getDependentQuestion(q, quizState)
  );
  const requiredAnsweredDependent = quizQuestions.filter((q) => {
    const dependentQuestion = getDependentQuestion(q, quizState);

    if (dependentQuestion) {
      return !!quizState.answers.find((a) => a.question === dependentQuestion.question)?.text;
    } else {
      return false;
    }
  });

  return requiredAnsweredTopLevel.length + requiredAnsweredDependent.length;
}

function getRequiredCount(quizState: QuizState) {
  const requiredTopLevel = quizQuestions.filter((q) => q.required).length;
  const requiredDependent = quizQuestions.filter((q) => {
    const existingAnswer = quizState.answers.find((a) => a.question === q.question)?.text;
    const dependentQuestion = q.dependentQuestions?.find((d) => d.condition === existingAnswer);

    return dependentQuestion && dependentQuestion.required;
  }).length;

  return requiredTopLevel + requiredDependent;
}

/**
 * Why the questionnaire cannot continue, shown against the answer that stops it. Scored -999 in
 * quiz.json: these are eligibility and consent stops rather than scoring.
 */
const BLOCKING_ANSWER_MESSAGES: Record<string, string> = {
  suicide:
    'Please call or text 988 to reach the Suicide and Crisis Lifeline. We cannot continue with an online evaluation while you are in crisis.',
  eighteen: 'We can only evaluate people who are 18 or older, so this questionnaire cannot continue.',
  terms_of_service: 'You need to accept the terms of service before you can submit.',
};

const BLOCKING_ANSWER_FALLBACK = 'We cannot continue with this answer.';

// Full-width cells with a 44px floor: comfortable thumb targets at 375px, and long metro names
// ("Miami/Fort Lauderdale") wrap inside the button instead of pushing the row sideways.
const ANSWER_BUTTON_CLASS_NAME = 'h-auto min-h-11 w-full whitespace-normal px-2 py-2 text-center leading-tight';

// 16px text keeps iOS Safari from zooming the page when a field takes focus.
const INPUT_CLASS_NAME = 'h-11 text-base sm:text-sm';

const AUTOCOMPLETE: Record<string, string | undefined> = {
  first_name: 'given-name',
  last_name: 'family-name',
  phone_number: 'tel',
};

function getAnswerGridClassName(answerCount: number) {
  if (answerCount === 3) return 'grid-cols-3';
  if (answerCount === 4) return 'grid-cols-2 sm:grid-cols-4';

  return 'grid-cols-2';
}

type PetsSectionProps = {
  pets: Pet[];
  updatePets: () => Promise<void>;
};

function PetsSection({ pets, updatePets }: PetsSectionProps) {
  const [deleting, setDeleting] = useState(false);

  return (
    <div
      className='relative flex flex-col gap-3'
      data-answer={pets.length || 'no_answer'}
      data-blocking={!pets.length}
      data-key='pets'
      data-question='pets'
    >
      <div className='flex flex-col'>
        <div className='flex justify-between'>
          <span className='flex-1'>Which pets need a letter?</span>
          <Check
            className={clsx({
              'text-slate-300': !pets.length,
              'text-green-600': pets.length,
            })}
          />
        </div>
        <span className='text-sm'>Add each pet you want covered.</span>
      </div>

      {!!pets.length && (
        <div className='flex flex-col gap-2'>
          {pets.map((pet) => (
            <div className='flex items-center justify-between gap-2 rounded-md border p-2' key={pet.id}>
              {/* Same shape as the dashboard pet cards, so it reads as one pet's details. */}
              <div className='flex min-w-0 flex-col'>
                <span className='text-sm font-semibold'>{pet.name}</span>
                <span className='text-sm'>
                  {[pet.species, pet.breed, pet.pounds && `${pet.pounds} lbs`].filter(Boolean).join(', ')}
                </span>
              </div>
              <div className='flex shrink-0'>
                <PetEditButton
                  doNotReload
                  onSubmitted={async () => {
                    await updatePets();

                    closeHighestDrawer();
                  }}
                  pet={pet}
                >
                  <Button aria-label={`Edit ${pet.name}`} className='h-11 w-11' size='icon' variant='ghost'>
                    <Edit size={18} />
                  </Button>
                </PetEditButton>
                <Button
                  aria-label={`Remove ${pet.name}`}
                  className='h-11 w-11'
                  disabled={deleting}
                  size='icon'
                  variant='ghost'
                  onClick={async () => {
                    setDeleting(true);

                    await deletePet(pet.id);

                    await updatePets();

                    setDeleting(false);
                  }}
                >
                  <Trash size={18} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/*
        The old copy asked how many pets someone had, which reads as a number to type. It is a
        list, and a second pet is easy to miss, so say the count back and ask for the next one.
      */}
      {!!pets.length && (
        <span className='text-sm'>
          {pets.length} {pets.length === 1 ? 'pet' : 'pets'} added. Have another pet? Add it now.
        </span>
      )}

      <PetEditButton
        doNotReload
        onSubmitted={async () => {
          await updatePets();

          closeHighestDrawer();
        }}
        track
      >
        <Button className='flex h-11 w-full gap-2' variant={pets.length ? 'outline' : 'default'}>
          <Plus size={16} /> <span>{pets.length ? 'Add another pet' : 'Add pet'}</span>
        </Button>
      </PetEditButton>
    </div>
  );
}

/** Scrolls to one question from the footer's "still needed" list and pulses it. */
export async function scrollToQuizQuestion(question: string) {
  const answerEl = document.querySelector(`#quiz-form [data-question="${CSS.escape(question)}"]`);

  if (!answerEl) return;

  answerEl.scrollIntoView({ behavior: 'smooth', block: 'center' });

  answerEl.classList.add('no-answer-pulse');

  await wait(7000);

  answerEl.classList.remove('no-answer-pulse');
}

export async function scrollToQuizKey(scrollToKey?: string) {
  if (scrollToKey) {
    const answerEl = document.querySelector(`[data-key="${scrollToKey}"]`);

    if (answerEl) {
      answerEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  } else {
    /**
     * Only the current step is in the DOM, so the first blocking element is the first thing on
     * this step that stops Continue/Submit. Walking the DOM rather than looking up by key also
     * copes with quiz.json reusing the `sad` key for two different questions.
     */
    const answerEl = document.querySelector('#quiz-form [data-blocking="true"]');

    if (answerEl) {
      answerEl.scrollIntoView({ behavior: 'smooth', block: 'center' });

      answerEl.classList.add('no-answer-pulse');

      await wait(7000);

      answerEl.classList.remove('no-answer-pulse');
    }
  }
}
